import * as React from "react";
import { MarketplaceReleaseInstance, ReleaseTableItem } from "../../types/release";
import { Slash } from "react-feather";
import { Tooltip } from "../../../../components/Tooltip";
import { Table } from "../../../../newcomponents/table/table";
import { KindColumn } from "../../../../newcomponents/table/columns/wellKnown/kindColumn";
import { CreatedColumn } from "../../../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TagsColumn } from "../../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { GvcColumn } from "../../../../newcomponents/table/columns/wellKnown/gvcColumn";
import { ReleaseNameDescriptionColumn } from "../../../../newcomponents/table/marketplace/releaseNameDescriptionColumn";
import { useDeploymentsReducer } from "../../../../components/detail/deploymentsReducer";
import { DEPLOYMENT_UPDATE_INTERVAL_MS } from "../../../../envVariables";
import { MarketplaceIcon } from "../../components/MarketplaceIcon";
import { workloadHelpers } from "../../../../mst/kinds/workload.helpers";
import { WorkloadReadinessColumn } from "../../../../newcomponents/table/columns/workload/readinessColumn";
import { linksOf, request } from "../../../../services/cpln";
import { Template } from "../../types/template";
import { getMarketplaceErrorMessage, getTemplateVersion } from "../../utils";
import { FormLabel } from "../../../../components/antd/form/FormLabel";
import { useAppSelector } from "../../../../store/hooks";
import { selectContext, selectOrg } from "../../../../store/contextSlice";

interface Props {
  release: MarketplaceReleaseInstance;
  usedGvcs: string[];
  items: any[];
  itemsNotFound: any[];
}
export const LegacyReleaseInfo: React.FC<Props> = ({ release, usedGvcs, items, itemsNotFound }) => {
  const { org, gvc } = useAppSelector(selectContext);

  const [template, setTemplate] = React.useState<Template>(null as any);
  const templateVersion = getTemplateVersion(template, release.version);

  React.useEffect(() => {
    fetchTemplate();
  }, []);

  async function fetchTemplate() {
    try {
      const { data } = await request({ service: "marketplace", url: `/template/${release.template}` });
      setTemplate(data);
    } catch (e) {
      const errorMessage = getMarketplaceErrorMessage(e);
      console.error(`Marketplace App Detail Info Fetch Template`, errorMessage);
    }
  }

  const { state, fetchDeployments } = useDeploymentsReducer();
  React.useEffect(() => {
    const unsubscribe = fetchDeployments(
      items.filter((i) => i.kind === "workload").map((i) => linksOf(i).self!),
      DEPLOYMENT_UPDATE_INTERVAL_MS,
    );
    return () => {
      unsubscribe();
    };
  }, [items.length]);

  const totalItemCount = items.length + itemsNotFound.length;

  const releaseTableItems = [...items, ...itemsNotFound].map((item) => {
    let health = workloadHelpers.getHealth([], item);
    let fetchedReadiness = false;
    const selfLink = linksOf(item).self!;
    if (item.kind === "workload") {
      health = workloadHelpers.getHealth(state.deploymentsMap[selfLink], item);
      if (state.requestedLinks.includes(selfLink)) {
        fetchedReadiness = true;
      }
    }

    return {
      ...item,
      health: health,
      readiness: health.text,
      fetchedReadiness: fetchedReadiness,
    };
  });

  return (
    //
    <>
      <div className="release-detail">
        <div className="mb-4 flex items-center">
          {template?.hasIcon !== false && (
            <div className="w-1/4">
              <div
                className="w-min px-4 py-2 rounded"
                style={{ backgroundColor: "white", color: "var(--color-gray-1600)" }}
              >
                <div className="flex items-center justify-center" style={{ width: 75 }}>
                  <MarketplaceIcon templateName={release.template} hasIcon={template?.hasIcon} className="object-fit" />
                </div>
              </div>
            </div>
          )}
          {templateVersion ? (
            <div className="w-1/4">
              <FormLabel>App Version</FormLabel>
              <div>v{templateVersion.appVersion}</div>
            </div>
          ) : null}

          <div className="w-1/4">
            <FormLabel>GVC{usedGvcs.length > 1 ? "s" : ""}</FormLabel>
            <div>
              {usedGvcs.length > 0 ? (
                usedGvcs.join(", ")
              ) : (
                <div className="flex items-center gap-1">
                  <Slash className={`color-danger`} size={16} />
                  <span>None</span>
                </div>
              )}
            </div>
          </div>
          <div className="w-1/4">
            <FormLabel>Item Count</FormLabel>
            <div>{totalItemCount}</div>
          </div>
        </div>
        <div className="mb-1">
          <FormLabel>Items</FormLabel>
        </div>
        <Table<ReleaseTableItem>
          tableId="release-detail-items"
          data={releaseTableItems}
          hideSettings
          columns={[
            ReleaseNameDescriptionColumn(undefined),
            {
              id: "customInfo",
              label: "Info",
              cell: (p) => {
                const item = p.row.original;
                if (!item || !item.kind) {
                  return <span />;
                }

                if (item.notFound === true) {
                  return (
                    <Tooltip title={"Item not found"}>
                      <div className="flex items-center gap-2">
                        <Slash className={`color-danger`} size={16} />
                        <span>Item not found</span>
                      </div>
                    </Tooltip>
                  );
                }

                if (item.kind === "workload") {
                  const _cell = WorkloadReadinessColumn().cell!;
                  return _cell(p as any);
                }

                return <span />;
              },
            },
            KindColumn(),
            GvcColumn(),
            TagsColumn(),
            CreatedColumn(),
            LastModifiedColumn(),
          ]}
        />
      </div>
    </>
  );
};
